import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axiosClient from '../../axios'
import { getSavedAuthData } from '../../axios/interceptor'

/**
 * Per-user UI dismissal flags. Free-form JSONB on the BE — the contract
 * is owned here. Keys are ISO 8601 timestamps so we can later display
 * "Acknowledged on X" in a settings panel.
 *
 * `null` (vs `undefined`) is reserved for "explicitly reset" if we ever
 * need an admin tool to force-show a notice again.
 */
export interface UiPreferences {
  aiActAcknowledgedAt?: string | null
  blotTourCompletedAt?: string | null
  homeTourCompletedAt?: string | null
  /** ISO date the user accepted the legal documents (CGU/CGV/confidentialité). */
  legalAcceptedAt?: string | null
  /** LEGAL_VERSION the user accepted — compared to re-prompt on new versions. */
  legalAcceptedVersion?: string | null
  [k: string]: unknown
}

const QUERY_KEY = ['user', 'ui-preferences'] as const

/** Returns the server-side prefs blob; refetches once when the user changes. */
export function useUiPreferences() {
  const { userId } = getSavedAuthData()
  return useQuery<UiPreferences>({
    queryKey: [...QUERY_KEY, userId],
    queryFn: async () => {
      const { data } = await axiosClient.get<UiPreferences>('/users/me/ui-preferences')
      return data || {}
    },
    // Prefs change at most a handful of times in a user's lifetime — cache
    // hard to avoid flashing the modal between mount and first fetch.
    staleTime: 5 * 60 * 1000,
    enabled: !!userId,
  })
}

/**
 * Patch the server-side blob (shallow-merged on the BE). Use to persist
 * "I've seen this notice" / "I've completed this tour" flags so the
 * dismissal sticks across devices, browser profiles and storage wipes.
 */
export function useUpdateUiPreferences() {
  const qc = useQueryClient()
  const { userId } = getSavedAuthData()
  return useMutation({
    mutationFn: async (patch: UiPreferences) => {
      const { data } = await axiosClient.patch<UiPreferences>('/users/me/ui-preferences', patch)
      return data
    },
    onSuccess: (data) => {
      qc.setQueryData([...QUERY_KEY, userId], data)
    },
  })
}
